import { useContext } from 'react'

import {
  Button, Form, ModalContext, SectionDivider, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useInvoiceIntegrationSettings from '@hooks/useInvoiceIntegrationSettings'
import type { IntegrationData } from '@hooks/useIntegrationData'
import type { IntegrationOrganization, IntegrationAccounts } from '@hooks/useInvoiceIntegrationSettings'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import EntitySelectBox from '@components/EntitySelectBox'

const MODAL_KEY = 'InvoiceIntegrationSettingsModal'

type ModalCallbacks = {
  closeModal: () => void,
  updateIntegration: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

type ModalPayload = {
  integrationData: IntegrationData,
  integrationOrganizations?: IntegrationOrganization[],
  integrationRevenueAccounts?: IntegrationAccounts[],
  integrationExpenseAccounts?: IntegrationAccounts[],
}

const InvoiceIntegrationSettingsModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, updateIntegration } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload: ModalPayload = digObject(modalData, MODAL_KEY, {})
  const {
    integrationData,
    integrationExpenseAccounts,
    integrationOrganizations,
    integrationRevenueAccounts,
  } = modalPayload

  const invoiceIntegrationSettingsPayload = useInvoiceIntegrationSettings({ integrationData })
  const {
    callbacks: { createOrUpdateIntegration: createOrEditFn },
    integrationForm: {
      entityState,
      handlers,
      saveEnabled,
    },
    loading,
    sortedServices,
    updating,
  } = invoiceIntegrationSettingsPayload

  const discounts = digObject(entityState, 'data.discounts', '')
  const organization = digObject(entityState, 'data.organisation', '')

  const updateIntegrationPayload = {
    callbacks: {
      action: createOrEditFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: 'Integration Updated',
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Integration" titleSecondLine="Settings" />

      <SidebarModal.Content>
        {!loading && (
          <Form>
            <Form.Field
              hideErrorMessage
              label="Linked Organisation"
            >
              <EntitySelectBox
                // @ts-expect-error TS2322
                customEntities={integrationOrganizations}
                name="data.organisation"
                sortDirection="asc"
                entityValue="short_code"
                sortKey="name"
                entityDisplayKey="name"
                value={organization}
                {...handlers}
              />
            </Form.Field>

            <SectionDivider boxProps={{ marginBottom: 'medium', marginTop: 'medium' }}>Chart of Accounts</SectionDivider>

            <Form.Field
              hideErrorMessage
              label="Discounts"
            >
              <EntitySelectBox
                // @ts-expect-error TS2322
                customEntities={integrationExpenseAccounts}
                name="data.discounts"
                sortDirection="asc"
                entityValue="account_id"
                sortKey="name"
                entityDisplayKey="name"
                value={discounts}
                {...handlers}
              />
            </Form.Field>

            {sortedServices.map((service) => {
              const { id, title } = service
              const selected = digObject(entityState, `data.services.${id}`, '')

              return (
                <Form.Field label={title} key={id} marginTop="large">
                  <EntitySelectBox
                    // @ts-expect-error TS2322
                    customEntities={integrationRevenueAccounts}
                    name={`data.services.${id}`}
                    sortDirection="asc"
                    entityValue="account_id"
                    sortKey="name"
                    entityDisplayKey="name"
                    value={selected}
                    {...handlers}
                  />
                </Form.Field>
              )
            })}
          </Form>
        )}
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={updating}
          // @ts-expect-error TS2345
          onClick={() => updateIntegration(updateIntegrationPayload)}
          size="large"
        >
          {saveEnabled ? 'Save Integration Settings' : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <InvoiceIntegrationSettingsModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
